import type { EditorialContent } from '@cobalt/utilities';
import { mapEditorialContent, mapEditorialContentMatchAtGlance } from '@cobalt/utilities';
import { language } from '@utilities/browser';
import { orderBy } from '@utilities/collection-utils';
import { loadFromAPIs } from '@utilities/fetch';
import { cobaltApiKey, cobaltApiUrl } from '@utilities/WindowContext';

export const loadEntityFromTagName = async (
  tagName: string,
  lang = language,
  signal?: AbortSignal,
): Promise<EditorialContent> => {
  const filters = {
    url: `${tagName}/${lang?.toUpperCase()}`,
    aggregator: 'lightpagejson',
  };
  const response = await loadFromAPIs('pages', filters, {
    apiKey: cobaltApiKey,
    apiUrl: cobaltApiUrl,
    signal,
  });

  return mapEditorialContent(response?.model?.data, response?.model?.nodes) ?? {};
};

export const loadMatchAtGlanceById = async (
  id: string,
  signal?: AbortSignal,
): Promise<ReturnType<typeof mapEditorialContentMatchAtGlance>> => {
  const response = await loadEntityByIdRaw(id, signal);

  return mapEditorialContentMatchAtGlance(response?.model?.data, response?.model?.nodes);
};

export const loadEntityById = async (id: string, signal?: AbortSignal): Promise<EditorialContent> => {
  const response = await loadEntityByIdRaw(id, signal);

  return mapEditorialContent(response?.model?.data, response?.model?.nodes) ?? {};
};

const loadEntityByIdRaw = async (id: string, signal?: AbortSignal) => {
  const filters = {
    aggregator: 'lightpagejson',
  };
  const response = await loadFromAPIs<any>(`pages/${id}`, filters, {
    apiKey: cobaltApiKey,
    apiUrl: cobaltApiUrl,
    signal,
  });

  return response;
};

export const loadWebContainerEntities = async (
  webContainer: string,
  lang = language,
  signal?: AbortSignal,
): Promise<EditorialContent[]> => {
  const filters = {
    url: `${webContainer}/${lang?.replace('pl', 'po').toUpperCase()}`,
    aggregator: 'lightpagejson',
  };
  const response = await loadFromAPIs('pages', filters, {
    apiKey: cobaltApiKey,
    apiUrl: cobaltApiUrl,
    signal,
  });

  const nodes = orderBy<any>(response?.model?.data?.links?.pagelink?.list ?? [], (el) => el.metadata.position).map(
    (el) => response?.model?.nodes?.[el.targetId],
  );

  return nodes?.map((nodeData) => mapEditorialContent(nodeData, null, true)) ?? [];
};

export const loadEntitiesByClassification = async (
  filters: { [key: string]: any },
  lang = language,
  signal?: AbortSignal,
): Promise<EditorialContent[]> => {
  filters = {
    ...filters,
    'param.attributes.language': lang,
    aggregator: 'lightnodejson',
  };
  const response = await loadFromAPIs('cachedsearch/build', filters, {
    apiKey: cobaltApiKey,
    apiUrl: cobaltApiUrl,
    hideFromWeb: true,
    signal,
  });

  return response?.result?.map(({ nodeData }) => mapEditorialContent(nodeData, null, true)) ?? [];
};
